import React from 'react';
import styles from './PhoneMockup.module.css';

interface PhoneMockupProps {
  /** Main image for the post / reel */
  image: string;
  /** Profile picture (falls back to image) */
  avatar?: string;
  /** Grid images — will fill 9 cells, repeats if fewer provided */
  gridImages?: string[];
  /** Instagram username */
  username?: string;
  /** Follower count display */
  followers?: string;
  /** Posts count */
  posts?: string;
  /** Show as reel */
  isReel?: boolean;
  /** Reel video src (mp4) — if provided, renders video instead of image */
  reelVideo?: string;
  /** Reel view count */
  views?: string;
  /** Additional class */
  className?: string;
  /** Scale */
  scale?: number;
}

/**
 * Fills an array to exactly `count` items by repeating elements.
 */
function fillArray(arr: string[], count: number): string[] {
  if (arr.length === 0) return [];
  const result: string[] = [];
  for (let i = 0; i < count; i++) {
    result.push(arr[i % arr.length]);
  }
  return result;
}

const PhoneMockup: React.FC<PhoneMockupProps> = ({
  image,
  avatar,
  gridImages,
  username = 'user_ofm',
  followers = '124K',
  posts = '47',
  isReel = false,
  reelVideo,
  views = '1.2M',
  className = '',
  scale = 1,
}) => {
  // Build 15-cell grid (5 rows) so it overflows and looks like a scrollable feed
  const rawGrid = gridImages && gridImages.length > 0 ? gridImages : [image];
  const grid9 = fillArray(rawGrid, 15);

  return (
    <div
      className={`${styles.phone} ${className}`}
      style={{ transform: `scale(${scale})` }}
    >
      {/* Dynamic Island */}
      <div className={styles.dynamicIsland} />

      {/* Screen */}
      <div className={styles.screen}>
        {isReel ? (
          /* ===== REEL VIEW ===== */
          <div className={styles.reel}>
            {reelVideo ? (
              <video
                src={reelVideo}
                className={styles.reelImage}
                autoPlay
                loop
                muted
                playsInline
              />
            ) : (
              <img src={image} alt="" className={styles.reelImage} loading="lazy" />
            )}
            <div className={styles.reelOverlay}>
              <div className={styles.reelTop}>
                <span className={styles.reelLabel}>Reels</span>
              </div>
              <div className={styles.reelBottom}>
                <div className={styles.reelUser}>
                  <div className={styles.reelAvatar}>
                    <img src={avatar || image} alt="" loading="lazy" />
                  </div>
                  <span className={styles.reelUsername}>{username}</span>
                  <span className={styles.reelFollow}>Follow</span>
                </div>
                <div className={styles.reelStats}>
                  <span>{views} views</span>
                </div>
              </div>
              <div className={styles.reelActions}>
                <div className={styles.reelAction}>
                  <div className={styles.heartIcon} />
                  <span>48.2K</span>
                </div>
                <div className={styles.reelAction}>
                  <div className={styles.commentIcon} />
                  <span>892</span>
                </div>
                <div className={styles.reelAction}>
                  <div className={styles.shareIcon} />
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ===== PROFILE VIEW ===== */
          <div className={styles.profile}>
            {/* IG Header */}
            <div className={styles.igHeader}>
              <span className={styles.igUsername}>{username}</span>
              <div className={styles.igHeaderDots}>
                <span /><span /><span />
              </div>
            </div>

            {/* Profile info */}
            <div className={styles.profileInfo}>
              <div className={styles.profileAvatar}>
                <img src={avatar || image} alt="" loading="lazy" />
              </div>
              <div className={styles.profileStats}>
                <div className={styles.stat}>
                  <strong>{posts}</strong>
                  <span>Posts</span>
                </div>
                <div className={styles.stat}>
                  <strong>{followers}</strong>
                  <span>Followers</span>
                </div>
                <div className={styles.stat}>
                  <strong>312</strong>
                  <span>Following</span>
                </div>
              </div>
            </div>

            {/* Bio */}
            <div className={styles.bio}>
              <span className={styles.bioName}>{username.replace(/_/g, ' ')}</span>
              <span className={styles.bioText}>Digital creator</span>
            </div>

            {/* Action buttons */}
            <div className={styles.profileActions}>
              <div className={styles.editBtn}>Edit profile</div>
              <div className={styles.shareBtn}>Share</div>
            </div>

            {/* Grid tabs */}
            <div className={styles.gridTabs}>
              <div className={`${styles.gridTab} ${styles.gridTabActive}`}>
                <div className={styles.gridIcon} />
              </div>
              <div className={styles.gridTab}>
                <div className={styles.reelsIcon} />
              </div>
            </div>

            {/* Photo grid — 9 cells */}
            <div className={styles.photoGrid}>
              {grid9.map((src, i) => (
                <div key={i} className={styles.gridItem}>
                  <img src={src} alt="" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhoneMockup;
